import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isValidRelativePath } from "@/utils/urlValidation";

/**
 * LoginButton Component
 *
 * Button that triggers Auth0 login flow.
 * Can be used anywhere in the app where login is needed.
 * Preserves query parameters (e.g., cloneFrom) when redirecting.
 */
export function LoginButton() {
  const { loginWithRedirect, isLoading } = useAuth0();
  const [searchParams] = useSearchParams();

  const handleLogin = () => {
    const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
    const returnToParam = searchParams.get("returnTo");

    // Decode and validate returnTo parameter to prevent open redirect vulnerabilities
    let returnTo: string;
    if (returnToParam) {
      try {
        const decoded = decodeURIComponent(returnToParam);
        // Validate that decoded returnTo is a safe relative path
        if (isValidRelativePath(decoded)) {
          returnTo = decoded;
        } else {
          returnTo = window.location.pathname + window.location.search;
        }
      } catch (error) {
        returnTo = window.location.pathname + window.location.search;
      }
    } else {
      returnTo = window.location.pathname + window.location.search;
    }

    loginWithRedirect({
      authorizationParams: {
        audience,
        scope: "openid profile email",
      },
      appState: {
        returnTo,
      },
    });
  };

  return (
    <Button onClick={handleLogin} disabled={isLoading} size="lg">
      {isLoading ? "Loading..." : "Log In"}
    </Button>
  );
}
